import { Fragment, useEffect, useRef, useState } from "react";

import { Button, Checkbox, Drawer, Textarea } from "@/components";
import {
  ArrowDown,
  Check,
  Edit,
  InfoCircle,
  Minus,
  Plus,
} from "@/components/Icons";
import styles from "@/styles/containers/product-details.module.scss";

import { number, object } from "yup";

import { CatalogHelpers, Currency, OMath } from "@/tools";
import { QuestionItem } from "./Step/QuestionItem";

import classNames from "classnames/bind";
import { useLocale, useTranslations } from "next-intl";
import { OrderStorage } from "@/storage/OrderStorage";
import { TextWithLineBreaks } from "@/components/TextWithLineBreaks";
const css = classNames.bind(styles);

const collectQuestions = (items, selectedOptions) => {
  let result = [];

  items.forEach((item) => {
    if (item.type === "question") {
      result.push(item);

      if (item.items?.length > 0)
        result = result.concat(collectQuestions(item.items, selectedOptions));
    }

    if (item.type === "option") {
      const isSelected = selectedOptions.includes(item.id);

      if (isSelected && item.items?.length > 0)
        result = result.concat(collectQuestions(item.items, selectedOptions));
    }
  });

  return result;
};

const getSelectedOptionsFromState = (items) => {
  let selected = [];

  items.forEach((item) => {
    if (item.type === "option" && item.selected) {
      selected.push(item.id);

      if (item.items?.length > 0)
        selected = selected.concat(getSelectedOptionsFromState(item.items));
    }

    if (item.items?.length > 0)
      selected = selected.concat(getSelectedOptionsFromState(item.items));
  });

  return selected;
};

const Questions = ({
  localProduct,
  completedQuestions,
  questionSelected,
  setQuestionSelected,
  highlightError,
}) => {
  const t = useTranslations();

  const CHOOSE_SIZE = t("chooseSize");
  const CHOOSE_ADDONS = t("additional");

  const hasSizes = localProduct.sizes.length > 1;
  const hasAddons = localProduct.extra_ingredients.length > 0;
  const hasItems = localProduct.items.filter((b) => b.items.length > 0);
  const selectedOptions = getSelectedOptionsFromState(localProduct.items);
  const allQuestions = collectQuestions(localProduct.items, selectedOptions);

  return (
    <div className={styles.questions} id="questions">
      {hasSizes && (
        <div
          className={css({
            question: true,
            active: questionSelected === CHOOSE_SIZE,
            done: completedQuestions.includes(CHOOSE_SIZE),
          })}
          onClick={() => setQuestionSelected(CHOOSE_SIZE)}
        >
          <span className={styles.number}>
            {completedQuestions.includes(CHOOSE_SIZE) ? <Check /> : 1}
          </span>
          <p className={styles.name}>{t("chooseSize")}</p>
          {completedQuestions.includes(CHOOSE_SIZE) && (
            <div className={styles.edit}>
              <Edit />
            </div>
          )}
        </div>
      )}

      {allQuestions.map((question, index) => {
        const max = question?.max;
        const selectedOptions = question.items.reduce(
          (t, o) => (max > 0 ? Math.min(max, t + o.quantity) : t + o.quantity),
          0,
        );

        return (
          <div
            className={css({
              question: true,
              active: questionSelected === question.id,
              done:
                selectedOptions >= question?.min &&
                selectedOptions <= question?.max,
              incomplete:
                highlightError &&
                !completedQuestions.includes(question.id) &&
                selectedOptions < question?.min,
            })}
            key={question.id}
            onClick={() => setQuestionSelected(question.id)}
          >
            <span className={styles.number}>
              {selectedOptions >= question?.min &&
              selectedOptions <= question?.max ? (
                <Check />
              ) : hasSizes ? (
                index + 2
              ) : (
                index + 1
              )}
            </span>
            <p className={styles.name}>
              {typeof question.name === "string"
                ? question.name
                : question.name[locale] || question.name["pt-br"]}
            </p>
            {completedQuestions.includes(question.id) && (
              <div className={styles.edit}>
                <Edit />
              </div>
            )}
          </div>
        );
      })}

      {hasAddons > 0 && (
        <div
          className={css({
            question: true,
            active: questionSelected === CHOOSE_ADDONS,
          })}
          onClick={() => setQuestionSelected(CHOOSE_ADDONS)}
        >
          <span className={styles.number}>
            {hasSizes && hasItems
              ? hasItems.length + 2
              : hasItems
                ? hasItems.length + 1
                : hasSizes
                  ? 2
                  : 1}
          </span>
          <p className={styles.name}>{t("additional")}</p>
          {completedQuestions.includes(CHOOSE_ADDONS) && (
            <div className={styles.edit}>
              <Edit />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Canvas = ({ imageUrl, isZoomable = false }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    const img = new Image();

    img.onload = function () {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      if (!isZoomable && (img.naturalWidth >= 2500 || img.naturalHeight >= 2500)) {
        canvas.width = img.naturalWidth / 5;
        canvas.height = img.naturalHeight / 5;
      }

      context.drawImage(img, 0, 0, canvas.width, canvas.height);
    };

    img.src = imageUrl;
  }, [imageUrl]);

  return <canvas ref={canvasRef} width={200} height={200} />;
};

const prepareProduct = (product) => {
  if ((product.sizes || []).filter((s) => s.selected).length == 0) {
    product.sizes[0].selected = true;
  }

  product.items = product.items.filter((p) => p.items.length > 0);

  product.selected = true;

  return product;
};

export default function Step({
  product,
  onSave,
  onClose,
  isUpdate,
  onSendOrder,
  cartItemIndex,
}) {
  const t = useTranslations();

  const locale = useLocale();

  const [localProduct, setLocalProduct] = useState(prepareProduct(product));
  const [valid, setValid] = useState(false);
  const [questionSelected, setQuestionSelected] = useState(
    localProduct.sizes.length > 1
      ? CHOOSE_SIZE
      : localProduct.items.length > 0
        ? localProduct.items[0]?.id
        : localProduct.extra_ingredients.length > 0
          ? CHOOSE_ADDONS
          : null,
  );

  const [completedQuestions, setCompletedQuestions] = useState([]);
  const [stepsQuantityValid, setStepsQuantityValid] = useState(true);
  const [highlightError, setHighlightError] = useState(null);
  const [openConfirmSend, setOpenConfirmSend] = useState(false);

  useEffect(() => {
    if (localProduct.sizes.length > 1) {
      setCompletedQuestions([...completedQuestions, CHOOSE_SIZE]);
    }

    if (localProduct.items.length > 0) {
      const completed = localProduct.items.flatMap((parentItem) =>
        parentItem.items.flatMap((childItem) => {
          const isCompleted =
            (parentItem.min > 0 && parentItem.min === childItem.quantity) ||
            (!parentItem.min && childItem.quantity);

          return isCompleted ? [parentItem.id] : [];
        }),
      );

      setCompletedQuestions((completedQuestions) => [
        ...completedQuestions,
        ...completed,
      ]);
    }
  }, [localProduct]);

  useEffect(() => {
    if (questionSelected) {
      const questionEl = document.querySelector(
        `.${styles.question}.${styles.active}`,
      );

      if (!questionEl) return;

      questionEl.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [questionSelected]);

  useEffect(() => {
    validate();
  }, [localProduct]);

  const validateExtrasSelected = () => {
    // Checa se há questões obrigatórias
    if (localProduct.items?.length > 0) {
      const selectedExtras =
        localProduct.items.filter((q) => {
          //Se não tem minimo, não está incompleto
          if (q.min == 0) {
            return false;
          }

          // Situação onde a etapa é obrigatória e a etapa não tem nenhum item disponível.
          if (q.min > 0 && q.items.length == 0) {
            return true;
          }

          //Se o não tem opções listadas, passa.
          if (q.items?.length == 0) {
            return false;
          }

          const seletedOptions = q.items.filter((o) => o.selected) || [];
          if (seletedOptions[0]?.quantity >= q.min) {
            return false;
          }

          const seletedOptionsQuantity = seletedOptions.reduce(
            (acc, selectedOption) => acc + selectedOption.quantity,
            0,
          );

          if (
            seletedOptionsQuantity < q.min ||
            seletedOptionsQuantity > q.max
          ) {
            return true;
          }

          // Se tem ao menos um item selecionado, não está incompleto
          return !q.items.find((o) => o.selected);
        }).length > 0;

      return selectedExtras;
    }
  };

  useEffect(() => {
    const validateQuestions = (obj, isActive = true) => {
      if (!isActive) return true;

      if (obj.type === "question" && obj.min > 0) {
        let sumOptions = 0;
        if (Array.isArray(obj.items))
          for (const child of obj.items)
            if (child.type === "option" && child.selected === true)
              sumOptions += child.quantity || 0;

        if (sumOptions < obj.min) {
          return false;
        }
      }

      if (Array.isArray(obj.items)) {
        for (const child of obj.items) {
          let newActive = isActive;
          if (child.type === "option")
            newActive = isActive && child.selected === true;

          if (!validateQuestions(child, newActive)) return false;
        }
      }

      return true;
    };

    setStepsQuantityValid(validateQuestions(localProduct));
  }, [localProduct]);

  const validate = async () => {
    const productSchema = object().shape({ quantity: number().required() });

    setValid(
      (await productSchema.isValid(localProduct)) && !validateExtrasSelected(),
    );
  };

  const onProductChange = (data) => {
    setLocalProduct({ ...localProduct, ...data });
    validate();
  };

  const uncheckZeroQuantity = (obj) => {
    if (obj?.hasOwnProperty("selected") && obj?.hasOwnProperty("quantity")) {
      if (obj.quantity === 0) obj.selected = false;
    }

    for (const key in obj) {
      if (typeof obj[key] === "object") {
        uncheckZeroQuantity(obj[key]);
      }
    }
  };

  const save = () => {
    uncheckZeroQuantity(localProduct);

    onSave && onSave(localProduct);
  };

  const calculateItemPrice = (product) => {
    return CatalogHelpers.calculateSelectedItemPrice(product);
  };

  const renderPictureSection = () => {
    return (
      <div className={styles.picture}>
        <div className={styles.close} onClick={() => onClose && onClose()}>
          <ArrowDown />
        </div>
        {localProduct.image && (
          <Canvas
            imageUrl={`${process.env.NEXT_PUBLIC_CDN_ASSETS}/${localProduct.imageZoomable || localProduct.image}`}
            isZoomable={!!localProduct.imageZoomable}
          />
        )}
      </div>
    );
  };

  const renderQuantitySection = () => {
    return (
      <div className={styles.quantityContainer}>
        <span
          className={`${styles.minus} ${
            localProduct.quantity <= 1 ? styles.disabled : styles.enabled
          }`}
          onClick={() =>
            localProduct.quantity > 1 &&
            onProductChange({ quantity: --localProduct.quantity })
          }
        >
          <Minus />
        </span>
        <p className={styles.qty}>{localProduct.quantity}</p>
        <span
          className={`${styles.plus} ${styles.enabled}`}
          onClick={() => onProductChange({ quantity: ++localProduct.quantity })}
        >
          <Plus />
        </span>
      </div>
    );
  };

  const renderQuestionsRecursive = (items, qIndexBase, onChange, ctx) => {
    return items?.map((item, idx) => {
      const handleChange = (updatedItem) => {
        items[idx] = updatedItem;
        onChange(items);
      };

      return (
        <div key={item.id || idx}>
          <QuestionItem
            questionIndex={qIndexBase.index++}
            questionSelected={ctx.questionSelected}
            question={item}
            onChange={handleChange}
            highlightError={highlightError}
            onChangeSelectedQuestion={(questionId, option) => {
              let nextQuestion = option?.items?.find(
                (i) => i.type === "question",
              );

              if (!nextQuestion) {
                const index = items.findIndex((q) => q.id === questionId);
                nextQuestion = items[index + 1];
              }

              if (!nextQuestion && ctx.hasAddons)
                nextQuestion = { id: CHOOSE_ADDONS };

              if (nextQuestion) ctx.setQuestionSelected(nextQuestion.id);

              if (!ctx.completedQuestions.includes(questionId)) {
                ctx.setCompletedQuestions([
                  ...ctx.completedQuestions,
                  questionId,
                ]);
              }
            }}
          />

          {item.items?.length > 0 &&
            renderQuestionsRecursive(
              item.items,
              qIndexBase,
              (updatedSub) => {
                item.items = updatedSub;
                handleChange(item);
              },
              ctx,
            )}
        </div>
      );
    });
  };

  const renderQuestionSection = () => {
    const qObj = { index: 0 };
    const hasAddons = localProduct.extra_ingredients.length > 0;

    return renderQuestionsRecursive(
      localProduct.items,
      qObj,
      (updatedQuestions) => {
        localProduct.items = updatedQuestions;
        onProductChange({ items: localProduct.items });
      },
      {
        hasAddons,
        questionSelected,
        setQuestionSelected,
        completedQuestions,
        setCompletedQuestions,
      },
    );
  };

  const renderSizesSection = () => {
    const selectedSize = localProduct.sizes.find((size) => size.selected);
    const isPromotionalPrice = selectedSize.promo_price;

    return (
      <>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTitle}>{t("chooseSize")}</p>
          <span className={styles.requiredField}>{t("required")}</span>
        </div>

        <div className={styles.sizes}>
          <span className={styles.sizePrice}>
            {isPromotionalPrice && (
              <>
                <strike className={styles.promoPrice}>
                  {Currency.formatCurrency(selectedSize.old_price)}
                </strike>
                &nbsp;&nbsp;
              </>
            )}
            {Currency.formatCurrency(selectedSize.price)}
          </span>

          {localProduct.sizes.map((size, sizeIndex) => (
            <div
              key={sizeIndex}
              className={`${styles.size} ${
                size.selected ? styles.selected : ""
              }`}
              onClick={() => {
                const hasItems = localProduct.items.filter(
                  (b) => b.items.length > 0,
                );
                const hasAddons = localProduct.extra_ingredients.length > 0;

                localProduct.sizes.forEach((s) => (s.selected = false));
                localProduct.sizes[sizeIndex].selected = true;
                onProductChange({ sizes: localProduct.sizes });

                if (!completedQuestions.includes(CHOOSE_SIZE)) {
                  setCompletedQuestions([...completedQuestions, CHOOSE_SIZE]);
                }

                setQuestionSelected(
                  hasItems
                    ? hasItems[0].id
                    : hasAddons
                      ? CHOOSE_ADDONS
                      : CHOOSE_SIZE,
                );
              }}
            >
              <p className={styles.name}>
                {typeof size.name === "string"
                  ? size.name
                  : size.name[locale] || size.name["pt-br"]}
              </p>
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderObservationSection = () => {
    return (
      <>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTitle}>{t("anyObservations")}</p>
        </div>

        <div className={styles.observationSection}>
          <Textarea
            onChange={(e) => onProductChange({ observation: e.target.value })}
            value={localProduct.observation || ""}
            placeholder={t("exampleRemoveCarrot")}
            className="mt-20"
            max={128}
          />

          <span className={styles.obsLength}>
            {localProduct?.observation?.length || 0}/128
          </span>
        </div>
      </>
    );
  };

  const renderExtrasSection = () => {
    const currentSize = localProduct.sizes.find((s) => s.selected);

    const extras = localProduct.extra_ingredients.map((extra) => {
      const extraSize = extra.sizes.find(
        (size) => size.ref_id === currentSize.ref_id,
      );

      return {
        ...extra,
        price: extraSize?.price || extra.price,
      };
    });

    return (
      <div className="d-flex flex-col">
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTitle}>{t("additional")}</p>
        </div>
        <div className={styles.optionItems}>
          {extras.map((e, eIndex) => {
            return (
              <div
                className={styles.optionItem}
                key={eIndex}
                onClick={() => {
                  extras[eIndex].selected = !extras[eIndex].selected;
                  onProductChange({
                    extra_ingredients: extras,
                  });
                }}
              >
                <div className="d-flex flex-col">
                  <p className={styles.optionName}>
                    {CatalogHelpers.getTranslation({
                      item: e,
                      field: "name",
                      locale,
                    })}
                  </p>
                  <div className="d-flex align-center mt-10">
                    <span className={styles.optionPrice}>
                      {`${Currency.formatCurrency(e.price)}`}
                    </span>
                  </div>
                </div>
                <Checkbox
                  value={CatalogHelpers.getTranslation({
                    item: e,
                    field: "name",
                    locale,
                  })}
                  checked={e.selected}
                  key={`extraingredient-${e.id}`}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const elementRef = useRef(null);

  const handleToggleClass = () => {
    if (elementRef.current) {
      elementRef.current.classList.toggle(styles.expand);
    }
  };

  const cart = OrderStorage.getOrder()?.cart;
  const cartHasProducts = cart?.catalog?.items?.length > 0;

  return (
    <>
      <div className={styles.productDetailsContainer}>
        <div className={styles.productDetails}>
          {renderPictureSection()}
          <div className={styles.scrollDetails}>
            <p className={styles.productName}>
              {CatalogHelpers.getTranslation({
                item: product,
                field: "name",
                locale,
              })}
            </p>
            <p
              className={styles.productDescription}
              ref={elementRef}
              onClick={handleToggleClass}
            >
              <TextWithLineBreaks
                text={CatalogHelpers.getTranslation({
                  item: product,
                  field: "description",
                  locale,
                })}
              />
            </p>

            <Questions
              localProduct={localProduct}
              completedQuestions={completedQuestions}
              questionSelected={questionSelected}
              setQuestionSelected={(question) => setQuestionSelected(question)}
              highlightError={highlightError}
            />

            <div className={styles.subtotal}>
              <p>{t("subtotal")}</p>
              <p className={styles.total}>
                {Currency.formatCurrency(calculateItemPrice(localProduct))}
              </p>
            </div>
          </div>
        </div>

        <div className={styles.productContainer}>
          <div className={styles.productChooses}>
            {localProduct.sizes.length > 1 &&
              questionSelected === CHOOSE_SIZE &&
              renderSizesSection()}
            {localProduct.items.length > 0 && renderQuestionSection()}
            {localProduct.extra_ingredients.length > 0 &&
              questionSelected === CHOOSE_ADDONS &&
              renderExtrasSection()}
            {valid && (
              <div className={styles.footer}>
                {renderQuantitySection()}

                
                {false && !cartHasProducts ? ( // Ocultando o botão temporariamente
                  <Button
                    design="secondary"
                    className="w-100 mr-10"
                    disabled={!valid}
                    onClick={() => {
                      // if (cartItemIndex !== null)
                      //   OrderStorage.updateCartItem(cartItemIndex, product);
                      // else OrderStorage.addItemToCart(product);

                      // onSendOrder?.();
                      setOpenConfirmSend(true);
                    }}
                  >
                    Pedir agora
                  </Button>
                ) : null}

                <Button
                  design="primary"
                  className="w-100"
                  style={{
                    ...((!valid || !stepsQuantityValid) && {
                      background: "#f0f0f0",
                      color: "#bfbfbf",
                    }),
                  }}
                  disabled={!valid}
                  onClick={() =>
                    valid && stepsQuantityValid
                      ? save()
                      : setHighlightError((prev) => prev + 1)
                  }
                >
                  {isUpdate ? t("update") : t("add")}
                </Button>
              </div>
            )}
            {renderObservationSection()}
          </div>
        </div>
      </div>

      <Drawer
        open={openConfirmSend}
        direction="bottom-center"
        setOpen={() => setOpenConfirmSend(false)}
      >
        <div className={styles.confirmSend}>
          <InfoCircle />

          <p className={styles.title}>Atenção</p>
          <p className={styles.message}>
            Você tem certeza que deseja enviar{" "}
            <strong>{product.quantity} unidade(s) </strong> de{" "}
            <span>"{product.name}"</span> para a cozinha?
          </p>
          <div className={styles.confirmSendFooter}>
            <Button
              design="secondary"
              className="w-100"
              onClick={() => setOpenConfirmSend(false)}
            >
              Cancelar
            </Button>
            <Button
              design="terciary"
              className="w-100"
              onClick={() => {
                setOpenConfirmSend(false);
                if (cartItemIndex !== null)
                  OrderStorage.updateCartItem(cartItemIndex, product);
                else OrderStorage.addItemToCart(product);

                onSendOrder?.();
              }}
            >
              Confirmar
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  );
}
